import { atlasAuthApi } from '../../../services/api/atlasAuthApi';
import type {
  PaginatedEnrichedEvents,
  SummaryResponse,
  ActiveUsersResponse,
  SessionsSummaryResponse,
  PaginatedCatalog,
  EventCatalogOut,
  EventCatalogCreate,
  EventCatalogUpdate,
  CleanupResponse,
  EnrichedEventFilters,
  CustomerSummaryResponse,
  UserSummaryResponse,
  CustomerAggregateResponse,
  UserAggregateResponse,
} from './analyticsTypes';

// --- Enriched events -------------------------------------------------------

export async function fetchEnrichedEvents(
  params: EnrichedEventFilters & { page?: number; page_size?: number },
): Promise<PaginatedEnrichedEvents> {
  const res = await atlasAuthApi.get<PaginatedEnrichedEvents>(
    '/analytics/admin/events/enriched',
    { params },
  );
  return res.data;
}

// --- Summary ---------------------------------------------------------------

export async function fetchSummary(params?: {
  since?: string;
  until?: string;
}): Promise<SummaryResponse> {
  const res = await atlasAuthApi.get<SummaryResponse>('/analytics/admin/summary', { params });
  return res.data;
}

// --- Active users ----------------------------------------------------------

export async function fetchActiveUsers(days: number = 30): Promise<ActiveUsersResponse> {
  const res = await atlasAuthApi.get<ActiveUsersResponse>('/analytics/admin/active-users', {
    params: { days },
  });
  return res.data;
}

export async function fetchSessionsSummary(params?: {
  since?: string;
  until?: string;
}): Promise<SessionsSummaryResponse> {
  const res = await atlasAuthApi.get<SessionsSummaryResponse>(
    '/analytics/admin/summary/sessions',
    { params },
  );
  return res.data;
}

// --- Event catalog CRUD ----------------------------------------------------

export async function fetchCatalog(params?: {
  page?: number;
  page_size?: number;
}): Promise<PaginatedCatalog> {
  const res = await atlasAuthApi.get<PaginatedCatalog>('/analytics/admin/catalog', { params });
  return res.data;
}

export async function createCatalogEntry(payload: EventCatalogCreate): Promise<EventCatalogOut> {
  const res = await atlasAuthApi.post<EventCatalogOut>('/analytics/admin/catalog', payload);
  return res.data;
}

export async function updateCatalogEntry(
  eventName: string,
  payload: EventCatalogUpdate,
): Promise<EventCatalogOut> {
  const res = await atlasAuthApi.put<EventCatalogOut>(
    `/analytics/admin/catalog/${encodeURIComponent(eventName)}`,
    payload,
  );
  return res.data;
}

export async function deleteCatalogEntry(eventName: string): Promise<void> {
  await atlasAuthApi.delete(
    `/analytics/admin/catalog/${encodeURIComponent(eventName)}`,
  );
}

// --- Summaries (top-level totals) ------------------------------------------

export async function fetchCustomerSummary(params?: {
  page?: number;
  page_size?: number;
  since?: string;
  until?: string;
}): Promise<CustomerSummaryResponse> {
  const res = await atlasAuthApi.get<CustomerSummaryResponse>(
    '/analytics/admin/summary/customers',
    { params },
  );
  return res.data;
}

export async function fetchUserSummary(params?: {
  page?: number;
  page_size?: number;
  since?: string;
  until?: string;
}): Promise<UserSummaryResponse> {
  const res = await atlasAuthApi.get<UserSummaryResponse>(
    '/analytics/admin/summary/users',
    { params },
  );
  return res.data;
}

// --- Aggregates (drill-through by event name) ------------------------------

export async function fetchCustomerAggregate(params?: {
  page?: number;
  page_size?: number;
  customer_id?: string;
  since?: string;
  until?: string;
}): Promise<CustomerAggregateResponse> {
  const res = await atlasAuthApi.get<CustomerAggregateResponse>(
    '/analytics/admin/aggregate/customers',
    { params },
  );
  return res.data;
}

export async function fetchUserAggregate(params?: {
  page?: number;
  page_size?: number;
  actor_id?: string;
  since?: string;
  until?: string;
}): Promise<UserAggregateResponse> {
  const res = await atlasAuthApi.get<UserAggregateResponse>(
    '/analytics/admin/aggregate/users',
    { params },
  );
  return res.data;
}

// --- Cleanup ---------------------------------------------------------------

export async function cleanupEvents(retentionDays: number): Promise<CleanupResponse> {
  const res = await atlasAuthApi.delete<CleanupResponse>('/analytics/admin/cleanup', {
    params: { retention_days: retentionDays },
  });
  return res.data;
}
